import { SchemaObject } from 'ajv';

import { bool, nullable, ref, SCHEMA_BASE, stringList } from './common';

/** Inherited from the parent report rather than chosen in the form. */
const inherited = {
  parent_job_id: ref('uuid'),
  artifact_path: ref('artifactPath'),
  workspace_id: ref('workspaceId'),
  project_name: { type: 'string', minLength: 1 },
  client_name: ref('clientName'),
  target_group_name: ref('targetGroupName'),
  countries: ref('countryList'),
  age_ranges: ref('ageRanges'),
  include_male: ref('fraction'),
  include_female: ref('fraction'),
  SansRejector: bool,
  custom_class_db_name: ref('customClassDbName'),
  competitive_set_folders: ref('categoryList'),
  competitive_set_products: ref('productList'),
};

const optimizationProperties = {
  ...inherited,
  report_type: { const: 'optimization' },
  reportUID: ref('uuid'),
  numCores: ref('numCores'),
  job_definition: ref('jobDefinition'),
  requestedBy: ref('requestedBy'),
  products_to_opt: stringList({ minItems: 1, maxItems: 1 }),
  ggvar_to_lock: stringList({ items: ref('ggvarKey') }),
  ggvar_min_list: { type: 'array', items: ref('ggvarValue') },
  ggvar_max_list: { type: 'array', items: ref('ggvarValue') },
  constraint_levels: {
    type: 'array',
    items: ref('constraintLevel'),
    minItems: 1,
    uniqueItems: true,
  },
  new_reference_flavors_allowed: {
    type: 'array',
    items: { enum: ['TRUE', 'FALSE'] },
    minItems: 1,
    uniqueItems: true,
  },
  rangeReff: stringList(),
  rangeReffRange: {
    type: 'object',
    additionalProperties: false,
    required: ['min', 'max'],
    properties: {
      min: { type: 'array', items: ref('tuningFraction') },
      max: { type: 'array', items: ref('tuningFraction') },
    },
    pairwiseOrdered: ['min', 'max'],
  },
  ref_flavor_to_lock: stringList(),
  flavors_to_remove: stringList(),
  show_pg_gNorm: bool,
  plotNorm: bool,
  show_tuning_plots: bool,
  include_gradients: { const: true },
  include_archetypes: { const: false },
  profilingEnabled: bool,
  slack_me: bool,
  pixel_list: { type: 'array', items: ref('nonEmptyString') },
  add_or_subtract: { const: 'lock' },
  run_flavor_opt: bool,
  run_texture_opt: bool,
  opt_file_to_load: { type: 'null' },
  report_id_to_inherit_parent_data_sources: ref('reportIdToInherit'),
};

const optimizationRequired = [
  'report_type',
  'reportUID',
  'numCores',
  'job_definition',
  'requestedBy',
  ...Object.keys(inherited).filter((k) => k !== 'custom_class_db_name'),
  'products_to_opt',
  'ggvar_to_lock',
  'ggvar_min_list',
  'ggvar_max_list',
  'constraint_levels',
  'new_reference_flavors_allowed',
  'rangeReff',
  'rangeReffRange',
  'ref_flavor_to_lock',
  'flavors_to_remove',
  'add_or_subtract',
  'run_flavor_opt',
  'run_texture_opt',
];

/** Rules shared by both optimization flavours. */
const optimizationRules = {
  complementaryFractions: ['include_female', 'include_male'],
  equalLengths: [
    ['ggvar_to_lock', 'ggvar_min_list', 'ggvar_max_list'],
    ['rangeReff', 'rangeReffRange.min', 'rangeReffRange.max'],
  ],
  pairwiseOrdered: ['ggvar_min_list', 'ggvar_max_list'],
  subsetOf: [['products_to_opt', 'competitive_set_products']],
  disjoint: ['ref_flavor_to_lock', 'flavors_to_remove', 'rangeReff'],
};

export const optimizationSchema: SchemaObject = {
  $id: `${SCHEMA_BASE}optimization.json`,
  title: 'Optimization report params',
  type: 'object',
  additionalProperties: false,
  required: optimizationRequired,
  properties: optimizationProperties,
  ...optimizationRules,
  // Flavor and texture optimization cannot both be off for a normal run.
  anyTrue: ['run_flavor_opt', 'run_texture_opt'],
};

export const mixAndMatchSchema: SchemaObject = {
  $id: `${SCHEMA_BASE}mix-and-match.json`,
  title: 'Mix & match report params',
  description:
    'An optimization run that combines versions from earlier optimization reports of the same parent.',
  type: 'object',
  additionalProperties: false,
  required: [...optimizationRequired, 'mix_and_match'],
  properties: {
    ...optimizationProperties,
    ggvar_to_lock: { type: 'array', maxItems: 0 },
    rangeReff: { type: 'array', maxItems: 0 },
    run_flavor_opt: { const: false },
    run_texture_opt: { const: false },
    mix_and_match: {
      type: 'array',
      minItems: 1,
      items: {
        type: 'object',
        additionalProperties: false,
        required: ['report_id', 'artifact_path', 'versions'],
        properties: {
          report_id: ref('uuid'),
          artifact_path: ref('artifactPath'),
          versions: stringList({ minItems: 1 }),
        },
      },
      uniqueItemsBy: 'report_id',
    },
  },
  ...optimizationRules,
};

export const updateProductsSchema: SchemaObject = {
  $id: `${SCHEMA_BASE}update-products.json`,
  title: 'Product update report params',
  type: 'object',
  additionalProperties: false,
  complementaryFractions: ['include_female', 'include_male'],
  subsetOf: [
    ['original_products', 'competitive_set_products'],
    ['incoming_products', 'competitive_set_products'],
    ['incoming_workspaces', 'client_allowed_workspaces'],
  ],
  disjoint: ['original_products', 'incoming_products'],
  required: [
    'report_type',
    'reportUID',
    'client_name',
    'project_name',
    'target_group_name',
    'workspace_id',
    'job_definition',
    'requestedBy',
    'numCores',
    'parent_job_id',
    'artifact_path',
    'original_products',
    'incoming_products',
    'competitive_set_products',
    'client_allowed_workspaces',
    'incoming_workspaces',
    'ensure_exact_pq',
    'version_controled_translation',
    'countries',
    'age_ranges',
    'include_male',
    'include_female',
  ],
  properties: {
    report_type: { const: 'update_products' },
    reportUID: ref('uuid'),
    client_name: ref('clientName'),
    project_name: { type: 'string', minLength: 1 },
    target_group_name: ref('targetGroupName'),
    workspace_id: ref('workspaceId'),
    job_definition: ref('jobDefinition'),
    requestedBy: ref('requestedBy'),
    numCores: ref('numCores'),
    parent_job_id: ref('uuid'),
    artifact_path: ref('artifactPath'),
    original_products: ref('productList'),
    incoming_products: stringList(),
    competitive_set_products: ref('productList'),
    client_allowed_workspaces: ref('workspaceList'),
    incoming_workspaces: stringList(),
    only_new_products_on_report: bool,
    ensure_exact_pq: bool,
    version_controled_translation: bool,
    plotNorm: bool,
    wipe_out_imputed_ggvars: bool,
    profilingEnabled: bool,
    report_id_to_inherit_parent_data_sources: ref('reportIdToInherit'),
    slack_me: bool,
    countries: ref('countryList'),
    age_ranges: ref('ageRanges'),
    include_male: ref('fraction'),
    include_female: ref('fraction'),
    SansRejector: bool,
    custom_class_db_name: nullable({ type: 'string' }),
    competitive_set_folders: ref('categoryList'),
    show_pg_gNorm: bool,
  },
  // The two update strategies are mutually exclusive; neither is a standard update.
  atMostOneTrue: ['ensure_exact_pq', 'version_controled_translation'],
};
